/**
 * How the granny flat module talks about the estate, the person the client lives with, and the
 * pension leader — the words, kept in one place so the cards, the rail, the recap and the advice
 * document cannot drift (review of the two-children file, 18 Oct 2026; owner, 19 Oct 2026).
 *
 * WHAT GOES TO THE HOST IS NOT LOST (owner, 19 Oct 2026). The home signed over, the cash paid or the
 * build paid for goes to the person the client lives with. It leaves the client's estate, but it is
 * not gone: so the estate figure is "what is left in their estate", and beside it, what is already
 * with that person. Who that person is comes from nowhere on the file, so the words stay neutral —
 * never "your child" or "the family" (it may be a friend). The fairness point for the other
 * children belongs to What could go wrong (strategyRisks' familyToKeepWhole), only where the file
 * records two or more children.
 *
 * Presentation only: no engine figure is changed here.
 */
import { householdVoice } from '../../utils/householdWords.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

/*
 * These words are for the screens only (the advice document writes its own), so they speak in the
 * reader's voice (owner, 21 Oct 2026): "you" to the client, the household's names to an adviser.
 */

/*
 * WHO THEY WOULD LIVE WITH (granny flat rebuild, 21 Oct 2026; review decision 2 as the owner ruled it). Step 1
 * asks, from the profile: one of their children (the profile counts them but holds no names), another family
 * member, or a friend (rare, but it happens). The words never invent a name: "your child", "their child",
 * "their family member", "their friend". Unanswered, they stay "the person they live with".
 */
export type GfHostKind = 'child' | 'family' | 'friend';

/** The step 1 answer, or null when it has not been given. */
export function gfHostKindOf(factFind: DraftFactFind | null | undefined): GfHostKind | null {
    const h = (factFind?.goals as { gfHost?: unknown } | undefined)?.gfHost;
    return h === 'child' || h === 'family' || h === 'friend' ? h : null;
}

/** How many children the profile records (no names are held). */
export function gfChildrenCount(factFind: DraftFactFind | null | undefined): number {
    return parseInt(String((factFind as { family?: { childrenCount?: unknown } } | null | undefined)?.family?.childrenCount ?? ''), 10) || 0;
}

/**
 * "your child" / "their child" when it is one of their children; "your family member", "your friend"; and
 * "your host" (to the client) or "their host" (to an adviser) while
 * step 1 is unanswered.
 */
export function gfHostPhrase(factFind: DraftFactFind | null | undefined): string {
    const v = householdVoice(factFind);
    const kind = gfHostKindOf(factFind);
    if (kind === 'child') return `${v.their} child`;
    if (kind === 'family') return `${v.their} family member`;
    if (kind === 'friend') return `${v.their} friend`;
    // Unanswered: "their host" — short, takes a possessive ("the home becomes their host’s"), and fits a child,
    // another relative or a friend alike (live review, 22 Oct 2026: "the person John and Kerry live with’s").
    return `${v.their} host`;
}

/** "your child’s home", or "the home you would live in" while step 1 is unanswered. */
export function gfHostHome(factFind: DraftFactFind | null | undefined): string {
    if (!gfHostKindOf(factFind)) return `the home ${householdVoice(factFind).they} would live in`;
    return `${gfHostPhrase(factFind)}’s home`;
}

/** The step 1 choices, worded from the profile (the child count, never a name). */
export function gfHostOptions(factFind: DraftFactFind | null | undefined): Array<{ id: GfHostKind; label: string }> {
    const v = householdVoice(factFind);
    const kids = gfChildrenCount(factFind);
    const childLabel = kids >= 2 ? `One of ${v.their} ${kids === 2 ? 'two' : kids} children` : kids === 1 ? `${v.Their} child` : `One of ${v.their} children`;
    return [
        { id: 'child', label: childLabel },
        { id: 'family', label: 'Another family member' },
        { id: 'friend', label: 'A friend' },
    ];
}

/** "your estate" to the client; "Colin and Shelly's estate", "Joe's estate" to an adviser. */
export function gfEstateWords(factFind: DraftFactFind | null | undefined): string {
    return `${householdVoice(factFind).possessive} estate`;
}

/**
 * What goes to the person they live with when the arrangement starts: the home's value, the cash and
 * the build (paid into that person's home). The value at the start, not grown.
 */
export function gfHostReceived(move: { amountPaid?: number; propertyTransferred?: number; construction?: number }): number {
    return Math.max(0, (Number(move.amountPaid) || 0) + (Number(move.propertyTransferred) || 0) + (Number(move.construction) || 0));
}

/** The line beside an estate figure: "Plus $832,857 already with the person Colin and Shelly live with." */
export function gfHostLine(amount: number, factFind: DraftFactFind | null | undefined, money: (n: number) => string): string | null {
    if (!(amount > 0)) return null;
    return `Plus ${money(Math.round(amount))} already with ${gfHostPhrase(factFind)}, given when the arrangement starts.`;
}

interface PensionLeaderLike {
    id?: string;
    recommendationLabels?: string[];
    snapshot?: { pension?: number; legacyValue?: number };
}

/**
 * The Best Age Pension card's honest sentence (review, 18 Oct 2026): where it leads ONLY because
 * every other way of setting it up stops the pension, say so — computed, never assumed. Null when
 * another plan also keeps a pension, or this card does not lead on the pension.
 */
export function gfPensionOnlyLeaderLine(
    leader: PensionLeaderLike,
    movers: PensionLeaderLike[],
    baseline: PensionLeaderLike | null | undefined,
    opts: { hostReceived: number; legacyYears: number; factFind: DraftFactFind | null | undefined; money: (n: number) => string },
): string | null {
    if (!(leader.recommendationLabels || []).includes('Optimal Age Pension')) return null;
    const own = Number(leader.snapshot?.pension) || 0;
    if (!(own > 0)) return null;
    const others = movers.filter((s) => s !== leader && (s.id == null || s.id !== leader.id));
    if (others.length === 0 || others.some((s) => (Number(s.snapshot?.pension) || 0) > 0)) return null;
    const basePension = Number(baseline?.snapshot?.pension) || 0;
    let line = `It leads on the Age Pension only because every other way of setting it up modelled here stops the pension${basePension > 0 ? '' : ', and not entering pays none either'}.`;
    const est = Number(leader.snapshot?.legacyValue);
    const baseEst = Number(baseline?.snapshot?.legacyValue);
    if (Number.isFinite(est) && Number.isFinite(baseEst) && baseEst - est >= 1) {
        const gap = baseEst - est;
        const toHost = Math.min(gap, Math.max(0, opts.hostReceived));
        line += ` It leaves ${opts.money(Math.round(gap))} less in ${gfEstateWords(opts.factFind)} than not entering after ${opts.legacyYears} ${opts.legacyYears === 1 ? 'year' : 'years'}${toHost > 0 ? `, though ${opts.money(Math.round(opts.hostReceived))} of what was given is with ${gfHostPhrase(opts.factFind)} rather than lost` : ''}.`;
    }
    return line;
}

/* ── Opening a named section from elsewhere in the app ─────────────────────────────────────────── */

/** The sessionStorage hand-off Home and the action plan set before opening the module (as Support at home's 'careCompanionReopenHomeCareStrategy'). */
export const GF_OPEN_SECTION_KEY = 'careCompanionGrannyFlatOpenSection';
export type GfSection = 'overview' | 'guide' | 'calculator';

/** Opens the module at a named section: `openGrannyFlatSection(setActivePage, 'guide')`. */
export function openGrannyFlatSection(setActivePage: (page: string) => void, section: GfSection): void {
    try { sessionStorage.setItem(GF_OPEN_SECTION_KEY, section); } catch { /* storage blocked: the overview opens */ }
    setActivePage('GrannyFlatPage');
}

/** Reads and clears the hand-off; null when nothing was asked for. */
export function takeGrannyFlatOpenSection(): GfSection | null {
    try {
        const v = sessionStorage.getItem(GF_OPEN_SECTION_KEY);
        if (v) sessionStorage.removeItem(GF_OPEN_SECTION_KEY);
        return v === 'guide' || v === 'calculator' || v === 'overview' ? v : null;
    } catch {
        return null;
    }
}

/**
 * The window event the menu fires when the page already open is chosen again. The layout does not
 * fire it yet (MainAppLayout.goTo — see the report); the module listens so that one line there
 * makes "Granny flat" in the menu return to the start.
 */
export const PAGE_RESELECTED_EVENT = 'acw:page-reselected';
